import { Box, Stack, Divider } from '@mui/material';
import { useCallback, useState, useEffect } from 'react';
import teamsPng from './images/teamsIcon.png';
import sfPng from './images/sfIcon.png';
import CloseIcon from '@mui/icons-material/Close';
import { StyledChip } from '../common/StyledChip';
import { TeamsRecipientsNestedSelector } from './TeamsRecipientsNestedSelector';
import { colors, Typography } from '@sweep-io/sweep-design';
import { TeamsRecipientInfo, TeamsRecipientStruct, TeamsRecipientType } from './teams/types';
import { telemetry } from '../../telemetry';
import { useIntegrations } from '../../hooks/useIntegrations';
import { NestedFieldsSelector } from '../common/fieldsSelectors/NestedFieldsSelector';
import { ChevronDown } from '@sweep-io/sweep-design/dist/icons';
import { useSweepFieldsInfo } from '../../sweep-fields/useSweepFieldsInfo';
import { SweepFieldsInfoMap } from '../../sweep-fields/sweepFieldsInfoReducer';
import { SfdcDisplayType } from '../../types/fields';
import { parseSweepFieldPolymorphicID } from '../common/fieldsSelectors/nestedFieldsSelectorHelper';
import { CenteredCircularProgress } from '../common/CenteredCircularProgress';

interface TeamsRecipientsChipArrayProps {
  recipients: TeamsRecipientStruct[];
  onChange: (recipients: TeamsRecipientInfo[]) => void;
  readonly?: boolean;
  customButtonText?: string;
  crmOrgId?: string;
  objectName?: string;
  showDealRoomOption?: boolean;
  usersOnly?: boolean;
  isAddTextFieldFromSf?: boolean;
}

export const TeamsRecipientsChipArray = ({
  recipients,
  onChange,
  readonly,
  customButtonText = 'Add Recipient',
  crmOrgId,
  objectName,
  showDealRoomOption = false,
  usersOnly,
  isAddTextFieldFromSf,
}: TeamsRecipientsChipArrayProps) => {
  const [recipientsWithLabels, setRecipientsWithLabels] = useState<TeamsRecipientInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { getTeamsRecipientLabelsForAutomation } = useIntegrations();
  const { getLabelsFromIds } = useSweepFieldsInfo();

  useEffect(() => {
    const fetchLabels = async () => {
      if (!recipients?.length) {
        setRecipientsWithLabels([]);
        return;
      }

      // Handle recipients without labels (from API) - only for Teams types
      const teamsRecipientsWithoutLabels = recipients.filter(
        (r) => !r.label && r.type !== TeamsRecipientType.USER_STORED_IN_SALESFORCE_FIELD,
      );
      let apiRecipientsWithLabels: TeamsRecipientInfo[] = [];

      if (teamsRecipientsWithoutLabels.length > 0) {
        try {
          setIsLoading(true);
          const recipientIds = teamsRecipientsWithoutLabels.map((r) => `${r.value}|${r.type}`);
          apiRecipientsWithLabels = await getTeamsRecipientLabelsForAutomation(recipientIds);
        } catch (error) {
          telemetry.captureError(error);
        } finally {
          setIsLoading(false);
        }
      }

      // Handle Salesforce recipients without labels
      const sfRecipientsWithoutLabels = recipients.filter(
        (r) => !r.label && r.type === TeamsRecipientType.USER_STORED_IN_SALESFORCE_FIELD,
      );
      let mappedSfRecipients: TeamsRecipientInfo[] = [];

      if (sfRecipientsWithoutLabels.length > 0) {
        try {
          setIsLoading(true);
          // Get all unique field IDs from all Salesforce recipients
          const allFieldIds = sfRecipientsWithoutLabels.flatMap((r) =>
            typeof r.value === 'object' && 'fieldIds' in r.value ? r.value.fieldIds : [],
          );
          const uniqueFieldIds = [...new Set(allFieldIds)];

          // Get labels for all unique field IDs
          const labels = getLabelsFromIds(uniqueFieldIds);
          const sweepFieldsLabels: SweepFieldsInfoMap = {};
          uniqueFieldIds.forEach((id, index) => {
            sweepFieldsLabels[id] = {
              label: labels[index] || id,
              objectName: id.split('.')[0],
              referenceTo: [],
            };
          });

          // Map each recipient to its proper labels
          mappedSfRecipients = sfRecipientsWithoutLabels.map((r) => {
            const fieldIds =
              typeof r.value === 'object' && 'fieldIds' in r.value ? r.value.fieldIds : [];

            let label = '';
            if (fieldIds.length > 0) {
              const objectName = sweepFieldsLabels[fieldIds[0]]?.objectName;
              label = [
                objectName || '',
                ...fieldIds.map((id) => sweepFieldsLabels[id]?.label || ''),
              ].join('.');
            }

            return {
              id: fieldIds,
              label: label || fieldIds.join('.'),
              type: TeamsRecipientType.USER_STORED_IN_SALESFORCE_FIELD,
            };
          });
        } catch (error) {
          telemetry.captureError(error);
        } finally {
          setIsLoading(false);
        }
      }

      // Handle recipients that already have labels (from select)
      const recipientsWithExistingLabels = recipients.filter((r) => r.label);
      const mappedRecipientsWithLabels = recipientsWithExistingLabels.map((r) => {
        const id =
          typeof r.value === 'object' && 'fieldIds' in r.value
            ? r.value.fieldIds
            : (r.value as string);

        return {
          id,
          label: r.label as string,
          type: r.type as TeamsRecipientType,
        };
      });

      // Combine all sets of recipients
      setRecipientsWithLabels([
        ...mappedRecipientsWithLabels,
        ...apiRecipientsWithLabels,
        ...mappedSfRecipients,
      ]);
    };

    fetchLabels();
  }, [recipients, getTeamsRecipientLabelsForAutomation, getLabelsFromIds]);

  const handleDeleteChip = useCallback(
    (recipientToDelete: TeamsRecipientInfo) => {
      const newRecipients = recipientsWithLabels.filter((r) => r.id !== recipientToDelete.id);
      onChange(newRecipients);
    },
    [recipientsWithLabels, onChange],
  );

  const returnChipWithProps = useCallback(
    (recipient: TeamsRecipientInfo, isDelete: boolean) => {
      let ChipProps: any = {
        key: recipient.id,
        label: recipient.label,
        title: recipient.label,
        size: 'small',
        icon: (
          <img
            src={
              recipient.type === TeamsRecipientType.USER_STORED_IN_SALESFORCE_FIELD
                ? sfPng
                : teamsPng
            }
            alt={`${recipient.type} icon`}
            style={{ width: '12px', marginLeft: '8px' }}
          />
        ),
      };

      if (isDelete) {
        ChipProps = {
          ...ChipProps,
          onDelete: () => handleDeleteChip(recipient),
          deleteIcon: <CloseIcon sx={{ color: '#000', fontSize: 12 }} />,
        };
      }

      return <StyledChip {...ChipProps} sx={{ textTransform: 'capitalize' }} key={ChipProps.key} />;
    },
    [handleDeleteChip],
  );

  const handleNewRecipient = useCallback(
    (recipient: TeamsRecipientInfo) => {
      onChange([...recipientsWithLabels, recipient]);
    },
    [recipientsWithLabels, onChange],
  );
  const filterByEndSelectionTextOrUser = (field: SweepField) => {
    const { isResolvable } = parseSweepFieldPolymorphicID(field.id || '');
    if (isResolvable) {
      return true;
    }

    if (isAddTextFieldFromSf && field.sfDisplayType === SfdcDisplayType.STRING) {
      return true;
    }
    if (field.sfDisplayType === SfdcDisplayType.REFERENCE) {
      return Boolean(field.objectNames?.includes('User'));
    }
    return false;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        sx={{
          minHeight: '40px',
          backgroundColor: colors.white,
          border: `1px solid ${colors.grey[300]}`,
          borderRadius: '4px',
          padding: '4px',
          alignContent: 'center',
          paddingLeft: '12px',
        }}
      >
        {recipientsWithLabels?.length === 0 && (
          <>
            <Typography variant={'body'} color={colors.grey[500]}>
              Users / Teams will appear here
            </Typography>
            {isLoading && <CenteredCircularProgress size={22} />}
          </>
        )}
        {recipientsWithLabels.map((recipient) => returnChipWithProps(recipient, !readonly))}
      </Stack>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TeamsRecipientsNestedSelector
          onChange={handleNewRecipient}
          readonly={readonly}
          customButtonText={customButtonText}
          selectedRecipients={recipientsWithLabels}
          showDealRoomOption={showDealRoomOption}
          usersOnly={usersOnly}
        />
        <Divider orientation="vertical" flexItem />
        <NestedFieldsSelector
          filterBy={filterByEndSelectionTextOrUser}
          readonly={readonly}
          crmOrgId={crmOrgId || ''}
          objectType={objectName || ''}
          nestedPath={[]}
          customButtonEndIcon={<ChevronDown size="medium" color={colors.blue[500]} />}
          useCustomButton
          customButtonText="Add Dynamic Recipients"
          customButtonSx={{
            height: '20px',
            color: colors.blue[500],
            border: 'none',
            textTransform: 'unset',
            p: '4px 0px',
            '& span.MuiTypography-root': {
              fontSize: '14px',
              fontWeight: 400,
            },
            '&:hover': {
              background: 'transparent',
              color: colors.blue[600],
            },
          }}
          onChange={(field) => {
            const recipient: TeamsRecipientInfo = {
              id: field.fieldIds,
              label: field.fieldLabels.join('.'),
              type: TeamsRecipientType.USER_STORED_IN_SALESFORCE_FIELD,
            };
            handleNewRecipient(recipient);
          }}
        />
      </Box>
    </Box>
  );
};
